import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Upload, Loader2, Trash2 } from 'lucide-react';
import { galleryAPI } from '../api';

const STATIC_IMAGES = [
  { url: '/front1.png', alt: 'GTY Fleet 1', static: true },
  { url: '/front2.png', alt: 'GTY Fleet 2', static: true },
  { url: '/front3.png', alt: 'GTY Fleet 3', static: true },
  { url: '/front4.png', alt: 'GTY Fleet 4', static: true },
  { url: '/front5.png', alt: 'GTY Fleet 5', static: true },
  { url: '/front6.png', alt: 'GTY Fleet 6', static: true },
  { url: '/front7.png', alt: 'GTY Fleet 7', static: true },
];

const UPLOAD_ROLES = ['Management', 'Owner', 'Co-Owner', 'Admin', 'Senior Operator', 'Expert Truck Driver', 'Senior Driver'];

const getUserFromToken = () => {
  try {
    const token = localStorage.getItem('gty_token');
    if (!token) return null;
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (payload.exp * 1000 < Date.now()) return null;
    return payload;
  } catch { return null; }
};

const Gallery = () => {
  const [images, setImages]         = useState(STATIC_IMAGES);
  const [lightbox, setLightbox]     = useState(null); // index
  const [uploading, setUploading]   = useState(false);
  const [dragging, setDragging]     = useState(false);
  const [deleting, setDeleting]     = useState(null);
  const dropRef                     = useRef(null);
  const user                        = getUserFromToken();
  const canUpload                   = user?.role_names?.some(r => UPLOAD_ROLES.includes(r));
  const isManagement                = user?.role_names?.some(r => ['Management','Owner','Co-Owner','Admin'].includes(r));

  const loadUploaded = useCallback(async () => {
    try {
      const r = await galleryAPI.getImages();
      const uploaded = r.data.map(img => ({
        url: galleryAPI.getImageUrl(img.filename),
        alt: img.original_name,
        id: img.id,
        static: false,
        uploader: img.uploader_name,
      }));
      setImages([...STATIC_IMAGES, ...uploaded]);
    } catch { /* no token — show static only */ }
  }, []);

  useEffect(() => {
    loadUploaded();
  }, [loadUploaded]);

  // Keyboard navigation
  useEffect(() => {
    if (lightbox === null) return;
    const handler = (e) => {
      if (e.key === 'Escape')      setLightbox(null);
      if (e.key === 'ArrowRight')  setLightbox(i => (i + 1) % images.length);
      if (e.key === 'ArrowLeft')   setLightbox(i => (i - 1 + images.length) % images.length);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightbox, images.length]);

  const uploadFile = async (file) => {
    if (!file.type.startsWith('image/')) { alert('Only image files allowed'); return; }
    setUploading(true);
    try {
      await galleryAPI.uploadImage(file);
      await loadUploaded();
    } catch (e) {
      alert(e.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) uploadFile(file);
  };

  const handleDelete = async (img, idx) => {
    if (!window.confirm('Delete this image?')) return;
    setDeleting(img.id);
    try {
      await galleryAPI.deleteImage(img.id);
      setImages(prev => prev.filter((_, i) => i !== idx));
      if (lightbox === idx) setLightbox(null);
    } catch { alert('Delete failed'); }
    finally { setDeleting(null); }
  };

  return (
    <div className="min-h-screen pt-24 pb-16 px-6 md:px-12 bg-[#0a0a0c]">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="mb-16" data-testid="gallery-header">
          <span className="overline">Our Fleet</span>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-black mt-4 tracking-tighter uppercase">Gallery</h1>
          <div className="border-l-4 border-purple-600 pl-6 mt-6 max-w-2xl">
            <p className="text-lg text-[#a1a1aa] leading-relaxed">
              Take a look at our professional fleet and the team that keeps GTY moving forward.
            </p>
          </div>
        </div>

        {/* Upload zone — only for authorized users */}
        {canUpload && (
          <div
            ref={dropRef}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => { if (!uploading) document.getElementById('gallery-file-input').click(); }}
            className={`mb-10 border-2 border-dashed rounded-xl p-10 flex flex-col items-center justify-center gap-3 cursor-pointer transition-colors ${
              dragging ? 'border-purple-400 bg-purple-900/20' : 'border-[#3f3f46] hover:border-purple-600 bg-[#18181b]'
            }`}
          >
            {uploading
              ? <><Loader2 className="w-8 h-8 animate-spin text-purple-400" /><p className="text-sm text-[#a1a1aa]">Uploading...</p></>
              : <><Upload className="w-8 h-8 text-[#71717a]" /><p className="text-sm text-[#a1a1aa]">Drag & drop an image, or <span className="text-purple-400 font-bold">click to browse</span></p><p className="text-xs text-[#52525b]">PNG, JPG, WEBP supported</p></>
            }
            <input id="gallery-file-input" type="file" accept="image/*" className="hidden"
              onChange={(e) => { const f = e.target.files[0]; if (f) uploadFile(f); e.target.value = ''; }} />
          </div>
        )}

        {/* Grid */}
        <div className="gallery-grid" data-testid="gallery-grid">
          {images.map((image, index) => (
            <motion.div
              key={image.url + index}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.4, delay: Math.min(index, 6) * 0.07 }}
              className="relative group overflow-hidden border border-white/10 hover:border-purple-500/50 transition-all cursor-pointer"
              data-testid={`gallery-item-${index}`}
              onClick={() => setLightbox(index)}
            >
              <img src={image.url} alt={image.alt} className="gallery-image group-hover:scale-105 transition-transform duration-500" />

              {/* Hover overlay */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-end p-3 pointer-events-none">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity text-xs text-white font-bold uppercase tracking-wider">
                  Click to expand
                </div>
              </div>

              {/* Delete button for management (uploaded only) */}
              {isManagement && !image.static && (
                <button
                  onClick={(e) => { e.stopPropagation(); handleDelete(image, index); }}
                  disabled={deleting === image.id}
                  className="absolute top-2 right-2 bg-black/70 hover:bg-red-700 p-1.5 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-auto"
                >
                  {deleting === image.id
                    ? <Loader2 className="w-3 h-3 animate-spin text-white" />
                    : <Trash2 className="w-3 h-3 text-white" />}
                </button>
              )}
            </motion.div>
          ))}
        </div>
      </div>

      {/* ── Lightbox ── */}
      <AnimatePresence>
        {lightbox !== null && (
          <motion.div
            key="lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
            onClick={() => setLightbox(null)}
          >
            {/* Close */}
            <button className="absolute top-4 right-4 text-white/70 hover:text-white p-2 z-10"
              onClick={() => setLightbox(null)}>
              <X className="w-7 h-7" />
            </button>

            {/* Prev */}
            <button
              className="absolute left-4 text-white/70 hover:text-white p-3 z-10 bg-black/40 hover:bg-black/70 rounded-full"
              onClick={(e) => { e.stopPropagation(); setLightbox(i => (i - 1 + images.length) % images.length); }}
            >
              <ChevronLeft className="w-7 h-7" />
            </button>

            {/* Image */}
            <motion.img
              key={lightbox}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              src={images[lightbox]?.url}
              alt={images[lightbox]?.alt}
              className="max-h-[90vh] max-w-[90vw] object-contain rounded shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />

            {/* Next */}
            <button
              className="absolute right-4 text-white/70 hover:text-white p-3 z-10 bg-black/40 hover:bg-black/70 rounded-full"
              onClick={(e) => { e.stopPropagation(); setLightbox(i => (i + 1) % images.length); }}
            >
              <ChevronRight className="w-7 h-7" />
            </button>

            {/* Counter */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-sm font-bold">
              {lightbox + 1} / {images.length}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Gallery;
